import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth.js';
import { startAutoPrivate } from '../utils/autoPrivate.js';
import { openSideWindow, isSideWindowOpen } from '../utils/sideWindow.js';

/* Screen-share privacy.
 *
 * A browser is never told that the screen is being shared — that happens in Teams or Zoom,
 * below the page — so this cannot switch itself on at the moment it is needed. It is on from
 * the start instead, for everybody, and the person turns it off when they mean to show the
 * numbers. Forgetting to turn it on is the failure that costs somebody their bank details;
 * forgetting to turn it off costs a click.
 *
 * It hides from the person sharing as well, and there is no version of this that does not. A
 * page draws one set of pixels and the share carries those same pixels, so there is no showing
 * somebody their own account number while hiding it from the room. Every capture API belongs to
 * the application doing the capturing, never to the page being captured; the only proposal that
 * would have let a page hide itself was rejected by the standards group precisely because it
 * takes the choice away from the person watching. So the answer here is a switch, not a trick.
 *
 * The state is per person and per device, not an account setting: one browser gets signed into
 * as several roles here, and one role's choice must not answer for another's. */

const PrivacyContext = createContext(null);

/* Version bumped because the meaning changed. The old key was written by the transient toggle
 * as well as by Preferences, so an "off" sitting in a browser today might be a deliberate
 * choice or might be one stray click during a call — and there is no telling which. Reading
 * them as deliberate is the unsafe guess, so none of them carry over; anyone who really wants
 * it off sets it once more, and this time only Preferences writes it. */
const keyFor = (userId) => `3ppms-privacy:v2:${userId ?? 'anon'}`;
const badgeKeyFor = (userId) => `3ppms-privacy-badge:${userId ?? 'anon'}`;

/* No badge, by default. It was tried on screen and Dhiraj watched it back from his phone
 * sitting on everybody else's; it was tried off and left him with no way to read his own
 * address. Neither is needed now that the window opens itself: the moment somebody clicks into
 * a blurred field they get the readable copy, which is the moment they wanted it. The switch
 * stays in Preferences for anyone who would rather have the badge as well. */
export const PRIVACY_KEY = 'Ctrl + Shift + H';

// The badge is opt-in: absent means nobody asked for it.
function readOffByDefault(key) {
    try {
        return localStorage.getItem(key) === 'on';
    } catch {
        return false;
    }
}

// Hiding is the default, so only an explicit 'off' turns it off.
function readOnByDefault(key) {
    try {
        return localStorage.getItem(key) !== 'off';
    } catch {
        return true;
    }
}



export function PrivacyProvider({ children }) {
    const { user } = useAuth();
    const userId = user?.id;
    const [enabled, setEnabled] = useState(true);
    const [badgeVisible, setBadgeVisibleState] = useState(false);

    // Re-read on sign-in/out, so the previous person's choice does not carry over to the next.
    useEffect(() => {
        setEnabled(readOnByDefault(keyFor(userId)));
        setBadgeVisibleState(readOffByDefault(badgeKeyFor(userId)));
    }, [userId]);

    useEffect(() => {
        document.documentElement.classList.toggle('privacy-on', enabled);
    }, [enabled]);

    // Runs whether or not the blur is on: tagging a field costs nothing while the CSS is off,
    // and it means the tags are already in place the moment somebody switches it back on.
    useEffect(() => startAutoPrivate(), []);

    /* Only a deliberate choice in Preferences is written down. Uncovering the fields from the
     * window or the shortcut is a person saying "let me read this one", not "stop protecting
     * me" — and it used to be recorded as the second. One click during a call left the blur off
     * in that browser for good, with nothing on screen to say so, and it stayed off for days.
     * A reload now brings it back, which is the behaviour anybody would assume it had. */
    const setPrivacy = useCallback((next, { persist = false } = {}) => {
        setEnabled(next);
        if (!persist) return;
        try {
            localStorage.setItem(keyFor(userId), next ? 'on' : 'off');
        } catch {
            /* private browsing — the class above still applies for this session */
        }
    }, [userId]);

    const setBadgeVisible = useCallback((next) => {
        setBadgeVisibleState(next);
        try {
            localStorage.setItem(badgeKeyFor(userId), next ? 'on' : 'off');
        } catch {
            /* private browsing — this session still honours it */
        }
    }, [userId]);

    const toggle = useCallback(() => setPrivacy(!enabled), [enabled, setPrivacy]);

    // The window outlives the render that opened it and keeps calling back in, so it reads
    // through refs rather than whatever was true at the time.
    const enabledRef = useRef(enabled);
    const toggleRef = useRef(toggle);
    enabledRef.current = enabled;
    toggleRef.current = toggle;

    /* Clicking into a blurred box is a person saying "I need to see this one", and it is also
     * the gesture a browser requires before it will open a window at all — asking for one out of
     * the blue is refused. So the two line up: the window arrives exactly when it is wanted,
     * without a badge sitting on the shared screen waiting to be noticed.
     *
     * pointerdown rather than focus, because activation has to still be live when the request
     * goes in, and by focusin the click may already have been spent. */
    useEffect(() => {
        if (!enabled) return undefined;
        const onDown = (e) => {
            if (!e.target?.closest?.('[data-private]')) return;
            if (isSideWindowOpen()) return;
            openSideWindow({ getHidden: () => enabledRef.current, onToggle: () => toggleRef.current() });
        };
        document.addEventListener('pointerdown', onDown, true);
        return () => document.removeEventListener('pointerdown', onDown, true);
    }, [enabled]);

    /* The way through when the badge is put away, and the quicker way even when it is not:
     * reaching for a floating button means the whole call watching the mouse travel to it. */
    useEffect(() => {
        const onKey = (e) => {
            if (!e.ctrlKey || !e.shiftKey || e.altKey) return;
            if ((e.key || '').toLowerCase() !== 'h') return;
            e.preventDefault();
            toggle();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [toggle]);

    return (
        <PrivacyContext.Provider value={{
            enabled,
            hidden: enabled,
            badgeVisible,
            setBadgeVisible,
            setPrivacy,
            toggle,
        }}>
            {children}
        </PrivacyContext.Provider>
    );
}

export function usePrivacy() {
    const context = useContext(PrivacyContext);
    if (!context) {
        throw new Error('usePrivacy must be used within a PrivacyProvider');
    }
    return context;
}

export default PrivacyContext;
